import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { QrCode, Pencil, Trash2, History } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cpApi } from '../api/api';
import { intlLocaleFor } from '../i18n/config';
import type { VndQrProfile } from '../types';
import VndPageLoading from '../components/VndPageLoading';
import DeleteConfirmationWidget from '../components/DeleteConfirmationWidget';
import { SepayTransactionsButton, SepayTransactionsWidget } from '../components/SepayTransactionsWidget';

function pct(used: number, limit: number): number {
  if (!limit || limit <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / limit) * 100)));
}

function isArchivedProfile(item: VndQrProfile): boolean {
  return Boolean(item.is_archived || item.deleted_at);
}

function issuedAccountNo(item: VndQrProfile): string {
  if (item.qr_account_mode === 'primary' && item.primary_account_no?.trim()) {
    return item.primary_account_no.trim();
  }
  return (item.bank_account_no ?? '').trim();
}

function confirmChannel(item: VndQrProfile): 'sms' | 'casso' | 'sepay' | 'none' {
  if (item.sms_enabled) return 'sms';
  if (item.work_mode !== 'auto') return 'none';
  return item.payment_provider === 'casso' ? 'casso' : 'sepay';
}

function channelLabel(channel: 'sms' | 'casso' | 'sepay' | 'none'): string {
  if (channel === 'sms') return 'SMS 🟡';
  if (channel === 'casso') return 'Casso 🟢';
  if (channel === 'sepay') return 'SePay 🔵';
  return '';
}

function enableLockedFromError(e: unknown): { message?: string; locked_by?: string; reason?: string } | null {
  if (!axios.isAxiosError(e) || e.response?.status !== 409) return null;
  const d = e.response.data as { error?: string; message?: string; locked_by?: string; reason?: string } | string | undefined;
  if (!d || typeof d === 'string') return null;
  if (d.error !== 'enable_locked') return null;
  return d;
}

function ProgressBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-700/80">
      <div
        className={`h-full rounded-full transition-all duration-500 ease-out ${className ?? 'bg-emerald-500'}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white px-2.5 py-2 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums leading-snug text-slate-800 dark:text-slate-100">{value}</div>
    </div>
  );
}

export default function VndQrProfilesPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFor(i18n.language);
  const formatVnd = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 0 });
  const location = useLocation();
  const [items, setItems] = useState<VndQrProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isResponsible, setIsResponsible] = useState(false);
  const [myCounterpartyId, setMyCounterpartyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'active' | 'deleted'>('active');
  const [deleteTarget, setDeleteTarget] = useState<VndQrProfile | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<VndQrProfile | null>(null);
  const [sepayTarget, setSepayTarget] = useState<VndQrProfile | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Record<number, boolean>>({});
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<Array<{ id: number; name: string; bank_name: string; bank_account_no?: string; created_at?: string }>>([]);
  const itemRefs = useRef<Record<number, HTMLElement | null>>({});

  const highlightedProfileId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('highlight');
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, pending] = await Promise.all([
        cpApi.getVndQrProfiles({ include_deleted: true }),
        cpApi.getVndQrAutoProfileRequests({ status: 'pending' }).catch(() => []),
      ]);
      setItems(data);
      setPendingRequests(pending);
    } catch {
      setError(t('qrProfiles.loadError'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const [profile, teamData] = await Promise.all([cpApi.getProfile(), cpApi.getVndTeam()]);
        setMyCounterpartyId(profile?.id ?? null);
        setIsResponsible(Boolean(profile?.id && teamData?.team?.responsible_counterparty_id === profile.id));
      } catch {
        setMyCounterpartyId(null);
        setIsResponsible(false);
      }
    })();
  }, []);

  useEffect(() => {
    const h = () => {
      void load();
    };
    window.addEventListener('vndQrProfilesChanged', h);
    return () => window.removeEventListener('vndQrProfilesChanged', h);
  }, [load]);

  useEffect(() => {
    const onModeChange = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: 'active' | 'deleted' }>).detail?.mode;
      if (mode === 'active' || mode === 'deleted') setViewMode(mode);
    };
    window.addEventListener('vndQrProfilesViewModeChange', onModeChange);
    return () => window.removeEventListener('vndQrProfilesViewModeChange', onModeChange);
  }, []);

  const setProfileActive = useCallback(async (profile: VndQrProfile, nextActive: boolean) => {
    const canToggle =
      isResponsible || (myCounterpartyId != null && profile.responsible_counterparty_id === myCounterpartyId);
    if (!canToggle) return;
    if (profile.deleted_at || profile.is_archived) return;
    setUpdatingIds((prev) => ({ ...prev, [profile.id]: true }));
    try {
      await cpApi.updateVndQrProfile(profile.id, { is_active: nextActive });
      setItems((prev) =>
        prev.map((it) => (it.id === profile.id ? { ...it, is_active: nextActive } : it))
      );
    } catch (e) {
      const locked = enableLockedFromError(e);
      if (locked) {
        const msg =
          locked.locked_by === 'system'
            ? t('qrProfiles.enableLockedSystem')
            : locked.locked_by === 'admin'
              ? t('qrProfiles.enableLockedAdmin')
              : locked.message || t('qrProfiles.enableLocked');
        window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'info' } }));
        await load();
        return;
      }
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const d = e.response.data;
        const fromApi = typeof d === 'string' ? d.trim() : '';
        setError(fromApi || t('qrProfiles.duplicateBankAccountActive'));
        await load();
        return;
      }
      setError(nextActive ? t('qrProfiles.toggleOnError') : t('qrProfiles.toggleOffError'));
    } finally {
      setUpdatingIds((prev) => {
        const next = { ...prev };
        delete next[profile.id];
        return next;
      });
    }
  }, [isResponsible, load, myCounterpartyId, t]);

  useEffect(() => {
    const onSetAll = (e: Event) => {
      const nextActive = (e as CustomEvent<{ is_active?: boolean }>).detail?.is_active;
      if (typeof nextActive !== 'boolean') return;
      if (bulkUpdating) return;
      const targets = items.filter((item) => !isArchivedProfile(item) && item.is_active !== nextActive);
      if (targets.length === 0) return;

      void (async () => {
        setBulkUpdating(true);
        window.dispatchEvent(
          new CustomEvent('vndQrProfilesBulkToggleState', {
            detail: { is_active: nextActive, loading: true },
          })
        );
        setError(null);
        try {
          const results = await Promise.allSettled(
            targets.map((item) => cpApi.updateVndQrProfile(item.id, { is_active: nextActive })),
          );
          const lockedN = results.filter((r) => r.status === 'rejected' && enableLockedFromError(r.reason)).length;
          const failedN = results.filter((r) => r.status === 'rejected' && !enableLockedFromError(r.reason)).length;
          await load();
          if (lockedN > 0 && nextActive) {
            window.dispatchEvent(
              new CustomEvent('showToast', { detail: { message: t('qrProfiles.bulkEnableLocked'), type: 'info' } }),
            );
          }
          if (failedN > 0) {
            const firstFail = results.find((r) => r.status === 'rejected' && !enableLockedFromError(r.reason));
            if (firstFail && firstFail.status === 'rejected' && axios.isAxiosError(firstFail.reason) && firstFail.reason.response?.status === 409) {
              const d = firstFail.reason.response.data;
              const fromApi = typeof d === 'string' ? d.trim() : '';
              setError(fromApi || t('qrProfiles.bulkOnDuplicateAccounts'));
            } else {
              setError(nextActive ? t('qrProfiles.bulkOnError') : t('qrProfiles.bulkOffError'));
            }
          }
        } catch (e) {
          if (axios.isAxiosError(e) && e.response?.status === 409) {
            const d = e.response.data;
            const fromApi = typeof d === 'string' ? d.trim() : '';
            setError(fromApi || t('qrProfiles.bulkOnDuplicateAccounts'));
          } else {
            setError(nextActive ? t('qrProfiles.bulkOnError') : t('qrProfiles.bulkOffError'));
          }
          await load();
        } finally {
          setBulkUpdating(false);
          window.dispatchEvent(
            new CustomEvent('vndQrProfilesBulkToggleState', {
              detail: { is_active: nextActive, loading: false },
            })
          );
        }
      })();
    };
    window.addEventListener('vndQrProfilesSetAllActive', onSetAll);
    return () => window.removeEventListener('vndQrProfilesSetAllActive', onSetAll);
  }, [bulkUpdating, items, load, t]);

  const startEdit = (item: VndQrProfile, focus?: 'sms' | 'audit') => {
    window.dispatchEvent(new CustomEvent('openVndQrProfileWidget', { detail: { edit: item, focus } }));
  };

  const onDelete = async (id: number) => {
    try {
      await cpApi.deleteVndQrProfile(id);
      await load();
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const d = e.response.data;
        const fromApi = typeof d === 'string' ? d.trim() : '';
        setError(fromApi || t('qrProfiles.deleteBlockedOpenRequests'));
        return;
      }
      setError(t('qrProfiles.deleteError'));
    }
  };

  const onRestore = async (id: number) => {
    try {
      await cpApi.restoreVndQrProfile(id);
      await load();
    } catch {
      setError(t('qrProfiles.restoreError'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    await onRestore(restoreTarget.id);
    setRestoreTarget(null);
  };

  const filteredItems = items.filter((item) =>
    viewMode === 'deleted' ? isArchivedProfile(item) : !isArchivedProfile(item)
  );

  useEffect(() => {
    if (highlightedProfileId == null) return;
    const highlighted = items.find((item) => item.id === highlightedProfileId);
    if (highlighted && isArchivedProfile(highlighted)) {
      setViewMode('deleted');
    } else if (highlighted) {
      setViewMode('active');
    }
  }, [highlightedProfileId, items]);

  useEffect(() => {
    if (highlightedProfileId == null) return;
    const node = itemRefs.current[highlightedProfileId];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedProfileId, filteredItems]);

  return (
    <div className="min-h-full w-full px-3 pb-4 pt-1 sm:px-4 sm:pb-6 sm:pt-2">
      <div className="w-full space-y-3">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {pendingRequests.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/30">
            <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
              {t('qrProfiles.pendingAdminTitle', { count: pendingRequests.length })}
            </p>
            <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-200/80">
              {t('qrProfiles.pendingAdminHint')}
            </p>
            <ul className="mt-2 space-y-1">
              {pendingRequests.map((req) => (
                <li key={req.id} className="text-xs text-amber-900 dark:text-amber-100">
                  #{req.id} — {req.name} (
                  <span translate="no" lang="en">
                    {req.bank_name}
                  </span>
                  , {req.bank_account_no || '—'})
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-slate-200/80 bg-white/80 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 overflow-hidden">
            <VndPageLoading fillTable />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-white p-10 text-center dark:border-slate-600 dark:from-slate-900/80 dark:to-slate-900/40">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <QrCode className="h-8 w-8" strokeWidth={1.5} />
            </div>
            <p className="mt-4 text-base font-medium text-slate-800 dark:text-slate-100">
              {viewMode === 'deleted' ? t('qrProfiles.emptyDeleted') : t('qrProfiles.emptyActive')}
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">
              {viewMode === 'deleted' ? t('qrProfiles.emptyDeletedHint') : t('qrProfiles.emptyActiveHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => {
                const dayPct = pct(item.used_today, item.daily_limit);
                const monthPct = pct(item.used_month, item.monthly_limit);
                const dayLeft = Math.max(0, (item.daily_limit ?? 0) - (item.used_today ?? 0));
                const monthLeft = Math.max(0, (item.monthly_limit ?? 0) - (item.used_month ?? 0));
                const isDeleted = isArchivedProfile(item);
                const canToggleByMember = !isResponsible && myCounterpartyId != null && item.responsible_counterparty_id === myCounterpartyId;
                const paymentProvider = item.payment_provider === 'casso' ? 'casso' : 'sepay';
                const channel = confirmChannel(item);
                const method = channelLabel(channel);
                const accountNo = issuedAccountNo(item);
                const rowTint =
                  channel === 'sms'
                    ? 'border-amber-300/80 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/10'
                    : channel === 'casso'
                      ? 'border-emerald-200/70 bg-emerald-50/40 dark:border-emerald-500/25 dark:bg-emerald-500/10'
                      : channel === 'sepay'
                        ? 'border-sky-200/70 bg-sky-50/40 dark:border-sky-500/25 dark:bg-sky-500/10'
                        : 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.04]';

                return (
                  <article
                    key={item.id}
                    ref={(node) => {
                      itemRefs.current[item.id] = node;
                    }}
                    className={`overflow-hidden rounded-lg border shadow-sm ${rowTint} ${
                      !item.is_active && !isDeleted ? 'opacity-80' : ''
                    } ${
                      highlightedProfileId === item.id
                        ? 'ring-2 ring-amber-300 dark:ring-amber-500/70'
                        : ''
                    }`}
                  >
                    <div className="border-b border-slate-100/80 bg-white/70 px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-base font-bold leading-tight text-slate-900 dark:text-white">{item.name}</h2>
                            <span
                              className="shrink-0 rounded-md border border-lime-400 bg-lime-200 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-lime-950 dark:border-lime-500/40 dark:bg-lime-500/20 dark:text-lime-100"
                              translate="no"
                              lang="en"
                            >
                              {item.bank_name}
                            </span>
                            {accountNo ? (
                              <span className="max-w-[12rem] truncate rounded-md border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px] leading-none text-slate-600 dark:border-white/15 dark:bg-white/5 dark:text-slate-300">
                                {accountNo}
                              </span>
                            ) : null}
                            {method ? (
                              <span className="rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">
                                {method}
                              </span>
                            ) : item.work_mode !== 'auto' ? (
                              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                                {t('qrProfiles.modeManual')}
                              </span>
                            ) : null}
                            {item.work_mode === 'auto' ? (
                              <span
                                className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                                  item.is_confirmed
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                }`}
                              >
                                {item.is_confirmed ? t('qrProfiles.confirmed') : t('qrProfiles.notConfirmed')}
                              </span>
                            ) : null}
                            {!item.is_active && !isDeleted && item.is_active_actor_kind && item.is_active_actor_kind !== 'counterparty' ? (
                              <span
                                className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                title={t('qrProfiles.enableLockedHint')}
                              >
                                {item.is_active_actor_kind === 'system'
                                  ? t('qrProfiles.enableLockedSystemShort')
                                  : t('qrProfiles.enableLockedAdminShort')}
                              </span>
                            ) : null}
                            {isDeleted ? (
                              <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                                {t('qrProfiles.badgeDeleted')}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {!isDeleted && item.work_mode === 'auto' ? (
                            <SepayTransactionsButton
                              provider={paymentProvider}
                              onClick={() => setSepayTarget(item)}
                            />
                          ) : null}
                          {isResponsible ? (
                            <button
                              type="button"
                              onClick={() => startEdit(item, 'audit')}
                              className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-violet-700 dark:border-white/15 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                              title={t('qrProfiles.history')}
                            >
                              <History className="h-4 w-4" />
                            </button>
                          ) : null}
                          {isResponsible ? (
                            <button
                              type="button"
                              onClick={() => startEdit(item)}
                              className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-indigo-700 dark:border-white/15 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                              title={t('qrProfiles.conditions')}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : null}
                          {isResponsible && isDeleted ? (
                            <button
                              type="button"
                              onClick={() => setRestoreTarget(item)}
                              className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-xs font-semibold text-emerald-800 shadow-sm hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                            >
                              {t('qrProfiles.restore')}
                            </button>
                          ) : null}
                          {isResponsible && !isDeleted ? (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(item)}
                              className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-white/15 dark:bg-white/5 dark:text-slate-400"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                          {!isDeleted && (isResponsible || canToggleByMember) ? (
                            <button
                              type="button"
                              aria-label={item.is_active ? t('qrProfiles.ariaDeactivate') : t('qrProfiles.ariaActivate')}
                              title={
                                !item.is_active && item.is_active_actor_kind && item.is_active_actor_kind !== 'counterparty'
                                  ? t('qrProfiles.enableLockedHint')
                                  : item.is_active
                                    ? t('qrProfiles.ariaDeactivate')
                                    : t('qrProfiles.ariaActivate')
                              }
                              onClick={() => {
                                void setProfileActive(item, !item.is_active);
                              }}
                              disabled={Boolean(updatingIds[item.id]) || bulkUpdating}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                item.is_active ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-600'
                              } ${Boolean(updatingIds[item.id]) || bulkUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                  item.is_active ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 px-4 py-3">
                      {item.work_mode === 'auto' &&
                      (item.account_holder_name || item.national_id || item.phone_number) ? (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <Metric label={t('qrProfiles.holderName')} value={item.account_holder_name || '—'} />
                          <Metric label={t('qrProfiles.nationalId')} value={item.national_id || '—'} />
                          <Metric label={t('qrProfiles.phone')} value={item.phone_number || '—'} />
                        </div>
                      ) : null}
                      <div className="rounded-lg bg-slate-50/90 p-3 ring-1 ring-slate-100 dark:bg-white/[0.04] dark:ring-white/10">
                        <div className="mb-2">
                          <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-medium text-slate-700 dark:text-slate-300">{t('qrProfiles.dayLimit')}</span>
                            <span className="tabular-nums text-slate-800 dark:text-slate-200">
                              <span className="font-semibold">{formatVnd(item.used_today)}</span>
                              <span className="text-slate-400"> / </span>
                              {formatVnd(item.daily_limit)}
                              <span className="ml-1 text-emerald-700 dark:text-emerald-400">
                                {t('qrProfiles.remaining', { n: formatVnd(dayLeft) })}
                              </span>
                            </span>
                          </div>
                          <ProgressBar value={dayPct} className="bg-gradient-to-r from-sky-400 to-blue-600" />
                        </div>
                        <div>
                          <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-medium text-slate-700 dark:text-slate-300">{t('qrProfiles.monthLimit')}</span>
                            <span className="tabular-nums text-slate-800 dark:text-slate-200">
                              <span className="font-semibold">{formatVnd(item.used_month)}</span>
                              <span className="text-slate-400"> / </span>
                              {formatVnd(item.monthly_limit)}
                              <span className="ml-1 text-violet-700 dark:text-violet-400">
                                {t('qrProfiles.remaining', { n: formatVnd(monthLeft) })}
                              </span>
                            </span>
                          </div>
                          <ProgressBar value={monthPct} className="bg-gradient-to-r from-violet-400 to-fuchsia-600" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                        <Metric
                          label={t('qrProfiles.ticks')}
                          value={t('qrProfiles.ticksFmt', { max: item.tick_max_requests ?? 0, win: item.tick_window_minutes ?? 0 })}
                        />
                        <Metric label={t('qrProfiles.perDay')} value={String(item.max_requests_per_day ?? 0)} />
                        <Metric label={t('qrProfiles.priority')} value={String(item.rule_priority ?? 100)} />
                        <Metric
                          label={t('qrProfiles.amountVnd')}
                          value={`${formatVnd(item.min_amount ?? 0)} — ${formatVnd(item.max_amount ?? 0)}`}
                        />
                        <Metric
                          label={t('qrProfiles.maxBalance')}
                          value={
                            Number(item.max_balance_vnd ?? 0) > 0
                              ? formatVnd(item.max_balance_vnd ?? 0)
                              : t('qrProfiles.maxBalanceNone')
                          }
                        />
                      </div>
                      {item.rule_comment ? (
                        <div className="rounded-lg border-l-4 border-indigo-300 bg-indigo-50/60 px-3 py-2 text-xs italic leading-relaxed text-slate-600 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-slate-300">
                          {item.rule_comment}
                        </div>
                      ) : null}
                    </div>
                  </article>
                );
              })}
          </div>
        )}
      </div>
      <DeleteConfirmationWidget
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          void confirmDelete();
        }}
        title={t('qrProfiles.deleteTitle')}
        message={t('qrProfiles.deleteMsg')}
        itemName={deleteTarget ? `${deleteTarget.name} (#${deleteTarget.id})` : undefined}
        confirmText={t('common.delete')}
        warningText={t('qrProfiles.deleteWarn')}
      />
      <DeleteConfirmationWidget
        isOpen={Boolean(restoreTarget)}
        onClose={() => setRestoreTarget(null)}
        onConfirm={() => {
          void confirmRestore();
        }}
        title={t('qrProfiles.restoreTitle')}
        message={t('qrProfiles.restoreMsg')}
        itemName={restoreTarget ? `${restoreTarget.name} (#${restoreTarget.id})` : undefined}
        confirmText={t('qrProfiles.restore')}
        warningText={t('qrProfiles.restoreWarn')}
      />
      {sepayTarget ? (
        <SepayTransactionsWidget
          open
          profileId={sepayTarget.id}
          profileName={sepayTarget.name}
          accountNo={sepayTarget.bank_account_no ?? ''}
          onClose={() => setSepayTarget(null)}
        />
      ) : null}
    </div>
  );
}
